import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import './CustomerDocumentUpload.css';
import config from '../config';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
const MAX_IMAGE_DIMENSION = 3000; // Maximum width or height in pixels

/**
 * Compresses an image file to be under 5MB
 * @param {File} file - The image file to compress
 * @returns {Promise<File>} - The compressed file
 */
const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    // If file is not an image or is already small enough, return as-is
    if (!file.type.startsWith('image/') || file.size <= MAX_FILE_SIZE) {
      resolve(file);
      return;
    }

    // If it's a PDF, return as-is (PDFs are handled differently)
    if (file.type === 'application/pdf') {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
          if (width > height) {
            height = (height / width) * MAX_IMAGE_DIMENSION;
            width = MAX_IMAGE_DIMENSION;
          } else {
            width = (width / height) * MAX_IMAGE_DIMENSION;
            height = MAX_IMAGE_DIMENSION;
          }
        }

        // Create canvas and compress
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Try different quality levels until we're under 5MB
        let quality = 0.9;
        const tryCompress = () => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }

              // If still too large and quality can be reduced, try again
              if (blob.size > MAX_FILE_SIZE && quality > 0.1) {
                quality -= 0.1;
                tryCompress();
              } else {
                // Create a new File object with the compressed blob
                const compressedFile = new File([blob], file.name, {
                  type: file.type,
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              }
            },
            file.type,
            quality
          );
        };

        tryCompress();
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

export default function CustomerDocumentUpload() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [utilityBillFile, setUtilityBillFile] = useState(null);
  const [passportFile, setPassportFile] = useState(null);
  const [utilityBillUploading, setUtilityBillUploading] = useState(false);
  const [passportUploading, setPassportUploading] = useState(false);
  const [utilityBillUploaded, setUtilityBillUploaded] = useState(false);
  const [passportUploaded, setPassportUploaded] = useState(false);
  const [utilityBillProgress, setUtilityBillProgress] = useState(0);
  const [passportProgress, setPassportProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid link. Please use the link provided in your email.');
      return;
    }

    // Check upload status on mount
    const checkUploadStatus = async () => {
      try {
        const response = await fetch(`${config.API_BASE_URL}/customers/upload/status?token=${encodeURIComponent(token)}`);
        if (response.ok) {
          const status = await response.json();
          if (status.utilityBillUploaded) {
            setUtilityBillUploaded(true);
            setUtilityBillProgress(100);
          }
          if (status.passportUploaded) {
            setPassportUploaded(true);
            setPassportProgress(100);
          }
        }
      } catch (err) {
        // Silently fail - status check is optional
        console.log('Could not check upload status:', err);
      }
    };

    checkUploadStatus();
  }, [token]);

  const handleFileSelect = async (type, file) => {
    if (!file) return;
    
    setError(null);
    
    try {
      // Compress image files if needed
      const processedFile = await compressImage(file);
      
      // Show file size info if compression occurred
      if (processedFile.size < file.size) {
        const originalSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        const compressedSizeMB = (processedFile.size / (1024 * 1024)).toFixed(2);
        console.log(`Image compressed from ${originalSizeMB}MB to ${compressedSizeMB}MB`);
      }
      
      if (type === 'utility') {
        setUtilityBillFile(processedFile);
        setUtilityBillUploaded(false);
        setUtilityBillProgress(0);
      } else if (type === 'passport') {
        setPassportFile(processedFile);
        setPassportUploaded(false);
        setPassportProgress(0);
      }
    } catch (err) {
      console.error('Error processing file:', err);
      setError('Failed to process file. Please try again.');
    }
  };

  const uploadFile = (file, fileType, onProgress) => {
    return new Promise((resolve, reject) => {
      if (!token) {
        reject(new Error('Token is required'));
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('token', token);
      formData.append('fileType', fileType);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          onProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (e) {
            resolve(xhr.responseText);
          }
        } else {
          reject(new Error(xhr.responseText || 'Upload failed'));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload cancelled'));
      });

      // Set timeout to 10 minutes (600000ms) for large file uploads
      xhr.timeout = 600000;
      
      xhr.addEventListener('timeout', () => {
        reject(new Error('Upload timeout - the file may be too large or the connection is too slow'));
      });

      xhr.open('POST', `${config.API_BASE_URL}/customers/upload`);
      xhr.send(formData);
    });
  };

  const handleUtilityBillUpload = async () => {
    if (!utilityBillFile) return;

    setUtilityBillUploading(true);
    setUtilityBillProgress(0);
    setError(null);
    try {
      await uploadFile(utilityBillFile, 'UTILITY_BILL', setUtilityBillProgress);
      setUtilityBillUploaded(true);
      setUtilityBillProgress(100);
    } catch (err) {
      setError(err.message || 'Failed to upload utility bill');
      setUtilityBillProgress(0);
    } finally {
      setUtilityBillUploading(false);
    }
  };

  const handlePassportUpload = async () => {
    if (!passportFile) return;

    setPassportUploading(true);
    setPassportProgress(0);
    setError(null);
    try {
      await uploadFile(passportFile, 'PASSPORT', setPassportProgress);
      setPassportUploaded(true);
      setPassportProgress(100);
    } catch (err) {
      setError(err.message || 'Failed to upload passport');
      setPassportProgress(0);
    } finally {
      setPassportUploading(false);
    }
  };

  useEffect(() => {
    if (utilityBillUploaded && passportUploaded) {
      setSuccess(true);
    }
  }, [utilityBillUploaded, passportUploaded]);

  if (!token) {
    return (
      <div className="vt-login">
        <div className="vt-loginShell">
          <div className="vt-loginCard vt-surface">
            <div className="vt-loginHeader">
              <h1 className="vt-loginTitle">Invalid Link</h1>
              <p className="vt-loginSubtitle vt-muted">Please use the link provided in your email to upload your documents.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="vt-login">
        <div className="vt-loginShell">
          <div className="vt-loginCard vt-surface" style={{ textAlign: 'center' }}>
            <div className="vt-loginHeader">
              <div style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%', 
                background: 'var(--vt-success)', 
                color: 'white', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: '48px', 
                fontWeight: 'bold',
                margin: '0 auto 24px'
              }}>
                ✓
              </div>
              <h1 className="vt-loginTitle" style={{ color: 'var(--vt-success)' }}>Documents Uploaded Successfully</h1>
              <p className="vt-loginSubtitle vt-muted">Thank you for uploading your documents. We will review them and get back to you soon.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vt-login">
      <div className="vt-loginShell">
        <div className="vt-loginCard vt-surface">
          <div className="vt-loginHeader">
            <h1 className="vt-loginTitle">Upload Your Documents</h1>
            <p className="vt-loginSubtitle vt-muted">Please upload your utility bill and passport (PDF or image)</p>
          </div>

          {error && (
            <div className="vt-loginError" style={{ marginBottom: '18px' }}>
              {error}
            </div>
          )}

          <div className="vt-loginForm">
            <div className="vt-field">
              <label className="vt-fieldLabel">Utility Bill</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="file"
                    id="utility-bill-input"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileSelect('utility', e.target.files[0])}
                    style={{ display: 'none' }}
                  />
                  <label 
                    htmlFor="utility-bill-input" 
                    className="vt-input"
                    style={{ 
                      cursor: 'pointer', 
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderStyle: 'dashed',
                      minHeight: '44px',
                      flex: '1',
                      minWidth: '200px'
                    }}
                  >
                    {utilityBillFile ? utilityBillFile.name : 'Choose utility bill file'}
                  </label>
                  {utilityBillFile && !utilityBillUploaded && (
                    <button
                      className="vt-btn vt-btn--primary"
                      onClick={handleUtilityBillUpload}
                      disabled={utilityBillUploading}
                      style={{ flexShrink: 0 }}
                    >
                      {utilityBillUploading ? 'Uploading...' : 'Upload'}
                    </button>
                  )}
                  {utilityBillUploaded && (
                    <span style={{ color: 'var(--vt-success)', fontWeight: '700', fontSize: '14px' }}>✓ Uploaded</span>
                  )}
                </div>
                {utilityBillUploading && (
                  <div style={{ width: '100%' }}>
                    <div style={{
                      width: '100%',
                      height: '8px',
                      backgroundColor: 'rgba(15, 23, 42, 0.08)',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${utilityBillProgress}%`,
                        height: '100%',
                        backgroundColor: 'var(--vt-accent)',
                        transition: 'width 0.2s ease',
                        borderRadius: '4px'
                      }} />
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: 'var(--vt-muted)', 
                      marginTop: '4px',
                      textAlign: 'right'
                    }}>
                      {Math.round(utilityBillProgress)}%
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="vt-field">
              <label className="vt-fieldLabel">Passport</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="file"
                    id="passport-input"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileSelect('passport', e.target.files[0])}
                    style={{ display: 'none' }}
                  />
                  <label 
                    htmlFor="passport-input" 
                    className="vt-input"
                    style={{ 
                      cursor: 'pointer', 
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderStyle: 'dashed',
                      minHeight: '44px',
                      flex: '1',
                      minWidth: '200px'
                    }}
                  >
                    {passportFile ? passportFile.name : 'Choose passport file'}
                  </label>
                  {passportFile && !passportUploaded && (
                    <button
                      className="vt-btn vt-btn--primary"
                      onClick={handlePassportUpload}
                      disabled={passportUploading}
                      style={{ flexShrink: 0 }}
                    >
                      {passportUploading ? 'Uploading...' : 'Upload'}
                    </button>
                  )}
                  {passportUploaded && (
                    <span style={{ color: 'var(--vt-success)', fontWeight: '700', fontSize: '14px' }}>✓ Uploaded</span>
                  )}
                </div>
                {passportUploading && (
                  <div style={{ width: '100%' }}>
                    <div style={{
                      width: '100%',
                      height: '8px',
                      backgroundColor: 'rgba(15, 23, 42, 0.08)',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${passportProgress}%`,
                        height: '100%',
                        backgroundColor: 'var(--vt-accent)',
                        transition: 'width 0.2s ease',
                        borderRadius: '4px'
                      }} />
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: 'var(--vt-muted)', 
                      marginTop: '4px',
                      textAlign: 'right'
                    }}>
                      {Math.round(passportProgress)}%
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
